import type { LoadingSpinnerTheme } from '$mds/LoadingSpinner';

// Const types fort storybook
export const ButtonVariant = [
  'primary',
  'secondary',
  'outline',
  'ghost',
  'destructive',
  'supporter',
  'shimmer',
  'pro',
  'apple'
] as const;
export const ButtonSize = ['xs', 'sm', 'md', 'lg'] as const;
export const TextColor = ['action', 'primary', 'secondary', 'alert'] as const;
export const ButtonType = ['button', 'submit', 'reset'] as const;
export const ButtonWidth = ['hug', 'full', 'min'] as const;
export const IconPosition = ['none', 'left', 'right'] as const;

// Spread const types for ts types
export type ButtonVariant = (typeof ButtonVariant)[number];
export type ButtonSize = (typeof ButtonSize)[number];
export type TextColor = (typeof TextColor)[number];
export type ButtonType = (typeof ButtonType)[number];
export type ButtonWidth = (typeof ButtonWidth)[number];
export type IconPosition = (typeof IconPosition)[number];

export interface ButtonVariantClasses {
  [key: string]: {
    default: string;
    spinner: LoadingSpinnerTheme;
    disabled: string;
  };
}

export const buttonVariantClasses: ButtonVariantClasses = {
  primary: {
    default:
      'bg-action-ondark-base !text-onlight-primary hover:bg-action-ondark-active focus-visible:bg-action-ondark-active',
    spinner: 'light',
    disabled: 'bg-action-ondark-inactive text-onlight-primary'
  },
  secondary: {
    default: 'bg-ondark-medium text-ondark-primary hover:bg-ondark-high focus-visible:bg-ondark-high',
    spinner: 'dark',
    disabled: 'bg-ondark-low text-action-ondark-inactive'
  },
  outline: {
    default: '!text-ondark-primary border border-ondark-base hover:bg-ondark-high focus-visible:bg-ondark-high',
    spinner: 'dark',
    disabled: 'text-action-ondark-inactive border border-ondark-base hover:bg-transparent'
  },
  ghost: {
    default: 'hover:bg-ondark-high focus-visible:bg-ondark-high',
    spinner: 'brand',
    disabled: 'hover:bg-transparent'
  },
  destructive: {
    default:
      'bg-alert-onlight-base text-ondark-primary hover:bg-alert-onlight-active focus-visible:bg-alert-onlight-active',
    spinner: 'dark',
    disabled: 'bg-alert-onlight-inactive text-ondark-primary'
  },
  supporter: {
    default:
      'bg-positive-ondark-base text-onlight-primary hover:bg-positive-ondark-active focus-visible:bg-positive-ondark-active hover:animate-shine hover:bg-gradient-shine bg-[length:200%_100%] hover:delay-[150ms] hover:animate-delay-[-200ms]',
    spinner: 'dark',
    disabled: 'bg-action-ondark-inactive text-onlight-primary'
  },
  shimmer: {
    default:
      'bg-gradient-shimmer-button bg-[length:210%_210%,210%_210%] bg-[position:center_0%,98.5%_center] text-onlight-primary hover:bg-[position:center_80%,2.5%_center] focus-visible:bg-[position:center_80%,2.5%_center] duration-500 ease-in-out hover:duration-700 hover:ease-spring-out',
    spinner: 'dark',
    disabled: 'text-onlight-primary bg-action-ondark-inactive'
  },
  pro: {
    default: 'bg-onlight-high hover:bg-onlight-low focus-visible:bg-onlight-low text-onlight-primary',
    spinner: 'dark',
    disabled: 'text-onlight-primary bg-action-ondark-inactive'
  },
  apple: {
    default: 'bg-black hover:bg-black focus-visible:bg-black text-ondark-primary',
    spinner: 'dark',
    disabled: 'text-action-ondark-inactive border border-ondark-base hover:bg-transparent'
  }
};

export const buttonHeightClasses = {
  xs: 'h-6',
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12'
};

export const iconButtonWidthClasses = {
  xs: 'w-6',
  sm: 'w-8',
  md: 'w-10',
  lg: 'w-12'
};

export const buttonWidthClasses = {
  min: 'min-w-28 px-5',
  hug: 'min-w-0 px-[10px]',
  full: 'w-full px-5'
};

export const buttonTextClasses = {
  xs: 'text-body-3xs',
  sm: 'text-body-3xs',
  md: 'text-body-xs',
  lg: 'text-body-xs'
};

export const buttonTextColorClasses = {
  action: {
    default: 'text-action-ondark-base',
    disabled: 'text-action-ondark-inactive',
    spinner: 'brand'
  },
  primary: {
    default: 'text-ondark-primary',
    disabled: 'text-action-ondark-inactive',
    spinner: 'dark'
  },
  secondary: {
    default: 'text-ondark-secondary',
    disabled: 'text-action-ondark-inactive',
    spinner: 'dark'
  },
  alert: {
    default: 'text-alert-ondark-base',
    disabled: 'text-action-ondark-inactive',
    spinner: 'dark'
  }
};
